import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import { formatEventRange, formatSegments } from '../events/labels';
import { Brand } from '../ui/Brand';
import { ANSWERS, dayLabel, shortDateTime, type Answer, type AnswerQuestion } from './types';
import './requests.css';
import { useUnsavedGuard } from '../ui/unsaved';
import { HistoryFold, PointLine, splitTimeline } from './Timeline';

function errorText(error: unknown): string {
  if (error instanceof ApiError && error.status === 404) return 'Ten link nie działa. Zapytaj osobę, która go wysłała.';
  if (error instanceof ApiError && error.status !== 0) {
    const details = Array.isArray(error.details) ? (error.details as { message?: unknown }[]).map((d) => d.message).filter((m): m is string => typeof m === 'string') : [];
    return details.length > 0 ? details.join('. ') : error.message;
  }
  return error instanceof ApiError ? error.message : 'Coś poszło nie tak. Spróbuj ponownie.';
}

type Body = { days: Record<string, boolean>; note: string } | { answer: Answer; note: string };
type WholeAnswer = 'moge' | 'nie_moge' | 'pytanie';
const WHOLE: WholeAnswer[] = ['moge', 'nie_moge', 'pytanie'];
/** What the person changed in one block (absent: as saved). */
type Draft = { dayChoice?: Record<string, boolean>; asking?: boolean; choice?: WholeAnswer; note?: string };
type Page = { questions: AnswerQuestion[] };

/** „2 zapytania”, „5 zapytań”. */
const questionsText = (n: number) => `${n} ${n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14) ? 'zapytania' : 'zapytań'}`;
/** The block's title: the event's name, or „Zapytanie o dni” for a question about days only. */
const titleOf = (q: AnswerQuestion) => q.event?.name ?? 'Zapytanie o dni';

const WARSAW_DAY = new Intl.DateTimeFormat('pl-PL', { timeZone: 'Europe/Warsaw', day: 'numeric', month: 'numeric', year: 'numeric' });
/** '2030-11-07' → '7.11.2030'. */
const dayText = (day: string) => `${Number(day.slice(8, 10))}.${Number(day.slice(5, 7))}.${day.slice(0, 4)}`;
/** Review M5: the block's days, „7.11.2030” or „8.11.2030 – 9.11.2030” (an old question without days: its event's). */
function rangeOf(q: AnswerQuestion): string {
  const days = (q.days ?? []).map((d) => d.day).sort();
  if (days.length === 0) return q.event ? [...new Set([WARSAW_DAY.format(new Date(q.event.startsAt)), WARSAW_DAY.format(new Date(q.event.endsAt))])].join(' – ') : '';
  return days.length === 1 ? dayText(days[0]!) : `${dayText(days[0]!)} – ${dayText(days.at(-1)!)}`;
}
/** How the page and its messages name a block: the title and its days. */
const nameOf = (q: AnswerQuestion) => `${titleOf(q)} ${rangeOf(q)}`.trim();

/**
 * One block as it would be sent: whether it changed from what is saved (switching back is no change), its body
 * (null: not complete yet) and, when touched and not complete, why (M4).
 */
function blockState(q: AnswerQuestion, draft: Draft | undefined) {
  const perDay = !!q.days && q.days.length > 0;
  const savedDays: Record<string, boolean> = Object.fromEntries((q.days ?? []).flatMap((d) => (typeof d.answer === 'boolean' ? [[d.day, d.answer] as const] : [])));
  const savedAsking = q.answer === 'pytanie';
  const savedCurrent: WholeAnswer | null = q.answer && q.answer !== 'czesciowo' ? q.answer : null;
  const savedNote = (q.answerNote ?? '').trim();
  const days = draft?.dayChoice ?? savedDays;
  const isAsking = draft?.asking ?? savedAsking;
  const current: WholeAnswer | null = draft?.choice ?? savedCurrent;
  const note = draft?.note ?? q.answerNote ?? '';
  const allDays = perDay && q.days!.every((d) => typeof days[d.day] === 'boolean');
  const noteChanged = note.trim() !== savedNote;
  const dirty = perDay ? isAsking !== savedAsking || (!isAsking && q.days!.some((d) => days[d.day] !== savedDays[d.day])) || noteChanged : current !== savedCurrent || noteChanged;
  const asks = isAsking || current === 'pytanie';
  let body: Body | null = null;
  let missing: string | null = null;
  if (asks && note.trim() === '') missing = 'wpisz pytanie';
  else if (perDay && isAsking) body = { answer: 'pytanie', note: note.trim() };
  else if (perDay) {
    // review M2 (owner 2026-10-30 01:30): only the days changed from what is shown (or not answered yet) — an untouched
    // day keeps its answer, and a newer mark of it stays as it is
    if (allDays) body = { days: Object.fromEntries(q.days!.filter((d) => days[d.day] !== savedDays[d.day]).map((d) => [d.day, days[d.day]!])), note: note.trim() };
    else missing = 'odpowiedz „Tak” albo „Nie” dla każdego dnia';
  } else if (current) body = { answer: current, note: note.trim() };
  else missing = 'wybierz odpowiedź';
  return { perDay, days, isAsking, current, note, allDays, dirty, body, missing: dirty ? missing : null };
}

/**
 * `/odpowiedz/:token` — the answer to availability questions, WITHOUT logging in (a technician without an account
 * opens it from the SMS or e-mail). Light, for the phone: the logo, then (owner 2026-10-28 12:57) a block for every
 * open question of the person — the opened one first — each with the event's name, dates and place (if any), the
 * message, and every asked day with „Tak” / „Nie”. A crew question also has „Mam pytanie”; an old one without days
 * the three whole answers. One „Zapisz odpowiedzi” sends the blocks filled in. Answers can be changed until the
 * event starts.
 */
export function AnswerPage() {
  const { token = '' } = useParams();
  const queryClient = useQueryClient();
  const key = ['answer-page', token];
  const url = `/api/requests/answer/${encodeURIComponent(token)}/all`;
  const page = useQuery({ queryKey: key, queryFn: () => api<Page>(url, { allowUnauthorized: true }) });
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [saved, setSaved] = useState(false);

  const answer = useMutation({
    mutationFn: (answers: Array<Body & { id: string; basedOn?: string }>) => api<Page>(url, { method: 'POST', body: { answers }, allowUnauthorized: true }),
    onSuccess: (data) => {
      queryClient.setQueryData(key, data);
      setDrafts({});
      setSaved(true);
    },
    // owner 2026-10-30 21:31: something newer happened since the page was read — the page as it is now (the choices stay, to send again)
    onError: (error) => {
      const current = error instanceof ApiError && error.code === 'history_moved' ? (error.current as Page | undefined) : undefined;
      if (current?.questions) queryClient.setQueryData(key, { questions: current.questions });
    },
  });

  const questions = page.data?.questions ?? [];
  const states = questions.map((q) => ({ q, ...blockState(q, drafts[q.id]) }));
  const touched = states.filter((s) => s.dirty);
  const dirty = touched.length > 0;
  // a block started and not finished holds the save back (it would be lost otherwise)
  // owner 2026-10-30 21:31: each answer names the newest point of its history the page showed (`basedOn`)
  const toSend = (): Array<Body & { id: string; basedOn?: string }> | null =>
    dirty && touched.every((s) => s.body) ? touched.map((s) => ({ id: s.q.id, ...s.body!, ...(s.q.latestPointId ? { basedOn: s.q.latestPointId } : {}) })) : null;
  // M4: what holds the save back, said by the button
  const blocking = touched.filter((s) => s.missing).map((s) => `${nameOf(s.q)} — ${s.missing}`);
  // M2 / M3: a refusal naming blocks is shown in each of them
  const errorIds = new Set(answer.error instanceof ApiError && answer.error.details && typeof answer.error.details === 'object' && 'ids' in answer.error.details ? ((answer.error.details as { ids?: unknown }).ids as string[]) : []);
  const change = (id: string, patch: Draft | null) => {
    setDrafts((d) => {
      const { [id]: _old, ...rest } = d;
      return patch ? { ...d, [id]: { ...d[id], ...patch } } : rest;
    });
    setSaved(false);
    answer.reset();
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const b = toSend();
    if (b) answer.mutate(b);
  };
  // owner 2026-10-23 21:14: an answer chosen and not sent: closing or reloading the page warns first (the browser's question)
  const guardRef = useUnsavedGuard({
    dirty,
    save: async () => {
      const b = toSend();
      if (!b) return false;
      await answer.mutateAsync(b);
      return true;
    },
  });
  const anyOpen = questions.some((q) => q.canAnswer);

  return (
    <main className="login answer-page">
      <div className="login-case answer-case">
        <div className="login-mark">
          <Brand large />
        </div>
        <div className="login-body">
          <h1>{questions.length > 1 ? 'Zapytania o dostępność' : 'Zapytanie o dostępność'}</h1>
          {page.isLoading && (
            <p className="muted" role="status">
              Wczytywanie…
            </p>
          )}
          {page.error && (
            <p className="alert" role="alert">
              {errorText(page.error)}
            </p>
          )}
          {page.data && (
            <form ref={guardRef} onSubmit={submit}>
              {questions.length > 1 && <p className="muted answer-intro">Masz {questionsText(questions.length)} — odpowiedz na każdy dzień osobno, a potem zapisz.</p>}
              {states.map((s) => (
                <QuestionBlock key={s.q.id} state={s} onChange={(patch) => change(s.q.id, patch)} error={errorIds.has(s.q.id) ? errorText(answer.error) : null} />
              ))}
              {saved && (
                <p className="answer-saved" role="status">
                  Dziękujemy! Odpowiedzi zapisane.
                </p>
              )}
              {answer.error && (
                <p className="alert" role="alert">
                  {errorText(answer.error)}
                </p>
              )}
              {blocking.length > 0 && (
                <p id="answer-blocking" className="muted answer-blocking">
                  Nie można jeszcze zapisać: {blocking.join('; ')}.
                </p>
              )}
              {anyOpen && (
                <button className="btn primary block" type="submit" disabled={!toSend() || answer.isPending} aria-describedby={blocking.length > 0 ? 'answer-blocking' : undefined}>
                  {answer.isPending ? 'Zapisywanie…' : 'Zapisz odpowiedzi'}
                </button>
              )}
            </form>
          )}
        </div>
      </div>
    </main>
  );
}

function QuestionBlock({ state, onChange, error }: { state: { q: AnswerQuestion } & ReturnType<typeof blockState>; onChange: (patch: Draft | null) => void; error: string | null }) {
  const { q, perDay, days, isAsking, current, note, allDays, dirty } = state;
  const place = q.event ? [q.event.location, q.event.locationAddress].filter((s) => s.trim() !== '').join(', ') : '';
  const headingId = `answer-block-${q.id}`;
  const range = rangeOf(q);
  // owner 2026-10-30 21:31: the current decision as before; the earlier points of the history folded below, read-only
  const { latest, earlier } = splitTimeline(q.history);
  return (
    <section className="answer-block" aria-labelledby={headingId} data-invalid={error ? 'true' : undefined}>
      <div className="answer-event">
        <h2 id={headingId}>
          {titleOf(q)} {range && <span className="answer-block-days">{range}</span>}
        </h2>
        {q.event && <p>{q.event.segments && q.event.segments.length > 1 ? `Terminy: ${formatSegments(q.event.segments)}` : formatEventRange(q.event.startsAt, q.event.endsAt)}</p>}
        {place && <p className="muted">{place}</p>}
      </div>
      <p className="answer-message">{q.message}</p>

      {q.answer && (
        <p className="answer-current">
          Twoja odpowiedź: <strong>{ANSWERS[q.answer]}</strong>
          {q.answeredAt ? ` (${shortDateTime(q.answeredAt)})` : ''}.{q.canAnswer ? (q.event ? ' Możesz ją zmienić do początku wydarzenia.' : ' Możesz ją zmienić.') : ''}
        </p>
      )}
      {latest && latest.kind !== 'sent' && latest.kind !== 'answer' && (
        <div className="answer-latest">
          <PointLine point={latest} as="div" />
        </div>
      )}
      {q.superseded && <p className="alert">To zapytanie jest nieaktualne — dostaniesz nowe.</p>}
      {!q.superseded && q.stale && <p className="alert">Termin wydarzenia się zmienił — poczekaj na nowe zapytanie.</p>}
      {q.closed && <p className="alert">Wydarzenie zostało odwołane — odpowiedź nie jest już potrzebna.</p>}
      {!q.closed && !q.stale && !q.superseded && !q.canAnswer && <p className="alert">{q.event ? 'Wydarzenie już się zaczęło — odpowiedzi nie można już zmienić.' : 'Te dni już minęły — odpowiedzi nie można już zmienić.'}</p>}

      {perDay && (
        <fieldset className="answer-days" disabled={!q.canAnswer || isAsking}>
          <legend>Czy możesz w te dni?</legend>
          {q.days!.map((d) => (
            <div key={d.day} className="answer-day" role="group" aria-label={dayLabel(d.day)}>
              <span className="answer-day-label">{dayLabel(d.day)}</span>
              {[true, false].map((yes) => (
                <button key={String(yes)} type="button" className="answer-choice small" data-answer={yes ? 'moge' : 'nie_moge'} aria-pressed={days[d.day] === yes} onClick={() => onChange({ dayChoice: { ...days, [d.day]: yes } })}>
                  {yes ? 'Tak' : 'Nie'}
                </button>
              ))}
            </div>
          ))}
        </fieldset>
      )}

      {q.canAnswer && (
        <>
          {perDay && q.kind === 'event' && (
            <label className="check answer-ask">
              <input type="checkbox" checked={isAsking} onChange={(e) => onChange({ asking: e.target.checked })} />
              <span>Mam pytanie (bez odpowiedzi na dni)</span>
            </label>
          )}
          {!perDay && (
            <div className="answer-choices" role="group" aria-label="Twoja odpowiedź">
              {WHOLE.map((a) => (
                <button key={a} type="button" className="answer-choice" data-answer={a} aria-pressed={current === a} onClick={() => onChange({ choice: a })}>
                  {ANSWERS[a]}
                </button>
              ))}
            </div>
          )}
          {(perDay || current) && (
            <label className="field">
              <span>{isAsking || current === 'pytanie' ? 'Twoje pytanie' : 'Uwagi (nieobowiązkowe)'}</span>
              <textarea value={note} onChange={(e) => onChange({ note: e.target.value })} rows={3} maxLength={1000} required={dirty && (isAsking || current === 'pytanie')} />
            </label>
          )}
          {perDay && !isAsking && !allDays && dirty && <p className="muted">Odpowiedz „Tak” albo „Nie” dla każdego dnia.</p>}
          {dirty && (
            <button type="button" className="btn ghost small answer-clear" onClick={() => onChange(null)}>
              Wyczyść
            </button>
          )}
        </>
      )}
      {error && <p className="alert">{error}</p>}
      <HistoryFold points={earlier} mine />
    </section>
  );
}
